import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  FormRenderer,
  toast,
} from "@eduviaos/ui-kit";
import { AlertCircle, ClipboardList, Loader2, UserPlus2 } from "lucide-react";
import {
  EMAIL_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  boundedNameSchema,
  emailSchema,
  fieldError,
  normalisePhone,
  phoneSchema,
  type AdmissionFunnelStage,
  type CreateEnquiryPayload,
  type EnquirySource,
  type FieldValidationError,
  type FileRef,
} from "@eduviaos/shared";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useCreateEnquiry, useFunnel } from "@/hooks/useAdmissions";
import { useFormForStage } from "@/hooks/useForms";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile } from "@/lib/verticalProfile";
import { draftRow, useAIDraftGenerator, type EnquiryDraft } from "@/lib/aiDraft";

/**
 * Beautiful, opinionated create-enquiry drawer.
 *
 * Phase H1 FE-1 — when the resolved initial stage has a linked
 * `FormDefinition` we render it inline with `<FormRenderer>` and ship
 * the collected values under `stage_payloads[<initial>]`. The H1 BE-2
 * gate (`PAYLOAD_MISSING` 422) means we cannot create the enquiry
 * without those values, so the submit button is disabled until the
 * form is loaded — and any field errors round-trip into the renderer
 * via `serverErrors`.
 *
 * Why a Sheet (right drawer) instead of a Dialog?
 *  - Form has 7 contact fields + an arbitrary stage form → too tall
 *    for a centered modal at 1080p without the page jiggling on tab
 *    focus.
 *  - The drawer keeps the AdmissionsPage list visible behind it so the
 *    IA can see "where it'll land" while typing.
 *
 * Validation strategy:
 *  - Local-first (HTML required + minLength) so we never POST a
 *    guaranteed-422.
 *  - BE-side errors come back as `PAYLOAD_INVALID` with `field_errors`;
 *    we surface contact-card errors inline and route stage-form
 *    errors into the FormRenderer's `serverErrors` channel.
 *
 * Source dropdown carries the BE-canonical values from
 * `@eduviaos/shared::EnquirySource` — keep in sync via the shared
 * type. New sources land automatically (BE accepts free-form strings)
 * but we limit the dropdown to the documented set so the IA's input
 * doesn't drift from the list filter's options.
 */

interface EnquiryCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new enquiry id after a successful POST. The list
   * cache is already invalidated; consumers usually navigate to the
   * detail page or pop a "Created — Open" toast here. */
  onCreated?: (enquiryId: string) => void;
}

const SOURCE_OPTIONS: { value: EnquirySource; label: string }[] = [
  { value: "walk_in", label: "Walk-in" },
  { value: "online_form", label: "Online form" },
  { value: "referral", label: "Referral" },
  { value: "phone", label: "Phone enquiry" },
  { value: "ad_campaign", label: "Ad campaign" },
  { value: "other", label: "Other" },
];

interface BeFieldError {
  field_key: string;
  code: string;
  message: string;
}

interface BeError {
  code?: string;
  message?: string;
  details?: { field_errors?: BeFieldError[] };
}

const EMPTY_STATE = {
  student_name: "",
  parent_name: "",
  student_email: "",
  student_phone: "",
  parent_phone: "",
  source: "" as EnquirySource | "",
  notes: "",
};

type LocalState = typeof EMPTY_STATE;

const PERSON_NAME_MAX = 120;
const NOTES_MAX = 2000;

/** Contact-card problems keyed by field; optional fields are checked only when filled. */
function validateContact(values: LocalState, personOne: string): Record<string, string> {
  const errors: Record<string, string> = {};
  const studentName = fieldError(
    boundedNameSchema(PERSON_NAME_MAX, `${personOne} name`),
    values.student_name,
  );
  if (studentName) errors.student_name = studentName;
  if (values.parent_name.trim()) {
    const parentName = fieldError(
      boundedNameSchema(PERSON_NAME_MAX, "Parent name"),
      values.parent_name,
    );
    if (parentName) errors.parent_name = parentName;
  }
  if (values.student_email.trim()) {
    const email = fieldError(emailSchema, values.student_email);
    if (email) errors.student_email = email;
  }
  for (const key of ["student_phone", "parent_phone"] as const) {
    if (values[key].trim()) {
      const phone = fieldError(phoneSchema, values[key]);
      if (phone) errors[key] = phone;
    }
  }
  if (values.notes.length > NOTES_MAX) {
    errors.notes = `Notes must be ${NOTES_MAX} characters or fewer.`;
  }
  return errors;
}

function sourceLabel(value: string): string {
  return SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** Fold an AI draft into the contact card. Interest has no field of its own,
 *  so it is kept in the notes where the admin can still edit it. */
function mergeEnquiryDraft(prev: LocalState, draft: EnquiryDraft, showParent: boolean): LocalState {
  const notes = [draft.interest ? `Interested in: ${draft.interest}` : null, draft.notes]
    .filter((part): part is string => Boolean(part))
    .join("\n");
  return {
    ...prev,
    student_name: draft.student_name ?? prev.student_name,
    student_email: draft.student_email ?? prev.student_email,
    student_phone: draft.student_phone ?? prev.student_phone,
    parent_name: showParent ? (draft.parent_name ?? prev.parent_name) : prev.parent_name,
    parent_phone: showParent ? (draft.parent_phone ?? prev.parent_phone) : prev.parent_phone,
    source: draft.source ?? prev.source,
    notes: notes ? notes.slice(0, NOTES_MAX) : prev.notes,
  };
}

function optionalPhone(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? normalisePhone(trimmed) : null;
}

/** The BE seeds the enquiry into the funnel's first non-terminal
 * stage when the caller omits `stage_key`. Mirror that resolution
 * client-side so we can pre-fetch the linked form for that stage and
 * render it without the user having to click "next". */
function resolveInitialStage(stages: AdmissionFunnelStage[] | undefined): string | null {
  if (!stages?.length) return null;
  const ordered = [...stages].sort((a, b) => a.order - b.order);
  const firstActive = ordered.find((s) => !s.is_terminal);
  return firstActive?.stage_key ?? null;
}

export function EnquiryCreateSheet({ open, onOpenChange, onCreated }: EnquiryCreateSheetProps) {
  const create = useCreateEnquiry();
  const funnelQuery = useFunnel();

  // SC Foundation (F0d) — de-school the create drawer per vertical. A
  // study centre captures "Learners" and has no parent/guardian, so the
  // whole Parent/Guardian block is dropped and the name field speaks in
  // learner vocabulary. School / coaching are byte-for-byte unchanged.
  const { vertical } = useEnabledModules();
  const profile = getVerticalProfile(vertical ?? "school");
  const personOne = profile.personNoun.one;
  const showParent = profile.has.parents;
  const initialStageKey = resolveInitialStage(funnelQuery.data?.stages);
  // Lazy-fetch the linked form ONLY while the sheet is open — the
  // list page mounts this component but doesn't open it on every
  // navigation, and we don't want a per-mount GET against /forms.
  const formQuery = useFormForStage(initialStageKey, { enabled: open });

  const [values, setValues] = React.useState<LocalState>(EMPTY_STATE);
  const [stagePayload, setStagePayload] = React.useState<Record<string, unknown>>({});
  const [stageFiles, setStageFiles] = React.useState<FileRef[]>([]);
  const [stageFieldErrors, setStageFieldErrors] = React.useState<FieldValidationError[]>([]);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [topLevelError, setTopLevelError] = React.useState<string | null>(null);
  const generateEnquiryDraft = useAIDraftGenerator("enquiry");
  const describeEnquiryDraft = React.useCallback(
    (draft: EnquiryDraft) => [
      ...draftRow(`${personOne} name`, draft.student_name),
      ...draftRow("Email", draft.student_email),
      ...draftRow("Phone", draft.student_phone),
      ...(showParent ? draftRow("Parent name", draft.parent_name) : []),
      ...(showParent ? draftRow("Parent phone", draft.parent_phone) : []),
      ...draftRow("Source", draft.source ? sourceLabel(draft.source) : null),
      ...draftRow("Interested in", draft.interest),
      ...draftRow("Notes", draft.notes),
    ],
    [personOne, showParent],
  );
  const applyEnquiryDraft = React.useCallback(
    (draft: EnquiryDraft) => {
      setValues((prev) => mergeEnquiryDraft(prev, draft, showParent));
      setFieldErrors({});
    },
    [showParent],
  );

  // Reset on every open so a previous create attempt's errors don't
  // persist across drawer dismissals (clicking outside leaves the
  // component mounted in the React tree).
  React.useEffect(() => {
    if (open) {
      setValues(EMPTY_STATE);
      setStagePayload({});
      setStageFiles([]);
      setStageFieldErrors([]);
      setFieldErrors({});
      setTopLevelError(null);
    }
  }, [open]);

  const set =
    <K extends keyof LocalState>(key: K) =>
    (v: LocalState[K]) => {
      setValues((prev) => ({ ...prev, [key]: v }));
      // Clear any prior error on the field as the user starts typing
      // again — keeps the inline message from looking stale.
      if (fieldErrors[key as string]) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[key as string];
          return next;
        });
      }
    };

  const submitFromRenderer = React.useCallback(
    async (payload: Record<string, unknown>, files: FileRef[]) => {
      // Mirror the values into our state so the next "Create" click
      // (or a re-submit after a server error) re-uses them. The
      // FormRenderer already calls onChange/onFilesChange in
      // controlled mode, so this is just a defensive sync.
      setStagePayload(payload);
      setStageFiles(files);
    },
    [],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setStageFieldErrors([]);
    setTopLevelError(null);

    const trimmedName = values.student_name.trim();
    const contactErrors = validateContact(values, personOne);
    if (Object.keys(contactErrors).length > 0) {
      setFieldErrors(contactErrors);
      return;
    }

    // The H1 BE-2 contract: when the initial stage has a linked form,
    // `stage_payloads[<initial>]` is mandatory. Bail out cleanly with
    // a single inline message instead of POSTing a guaranteed 422.
    if (formQuery.data && !initialStageKey) {
      setTopLevelError(
        "Couldn't determine the initial stage for the funnel — refresh and try again.",
      );
      return;
    }

    const payload: CreateEnquiryPayload = {
      student_name: trimmedName,
      parent_name: values.parent_name.trim() || null,
      student_email: values.student_email.trim() || null,
      student_phone: optionalPhone(values.student_phone),
      parent_phone: optionalPhone(values.parent_phone),
      source: values.source || null,
      notes: values.notes.trim() || null,
    };

    if (formQuery.data && initialStageKey) {
      payload.stage_payloads = { [initialStageKey]: stagePayload };
      if (stageFiles.length > 0) {
        payload.stage_file_refs = {
          [initialStageKey]: stageFiles.map((f) => ({ ...f })),
        };
      }
    }

    try {
      const created = await create.mutateAsync(payload);
      toast.success(`Created enquiry for ${trimmedName}.`);
      onCreated?.(created.id);
      onOpenChange(false);
    } catch (err) {
      const be = err as BeError;
      if (be?.code === "PAYLOAD_INVALID" || be?.code === "PAYLOAD_MISSING") {
        const fe = be.details?.field_errors ?? [];
        const contactKeys = new Set([
          "student_name",
          "parent_name",
          "student_email",
          "student_phone",
          "parent_phone",
          "source",
          "notes",
        ]);
        const contactErrors: Record<string, string> = {};
        const stageErrors: FieldValidationError[] = [];
        for (const e of fe) {
          if (contactKeys.has(e.field_key)) {
            contactErrors[e.field_key] = e.message;
          } else {
            stageErrors.push(e);
          }
        }
        setFieldErrors(contactErrors);
        setStageFieldErrors(stageErrors);
        setTopLevelError(
          fe.length > 0
            ? `${fe.length} field${fe.length === 1 ? "" : "s"} need attention before creating.`
            : (be.message ?? "The form needs to be completed before creating the enquiry."),
        );
        return;
      }
      setTopLevelError(be?.message ?? "Couldn't create the enquiry. Please try again.");
    }
  };

  const isBusy = create.isPending;
  const isFormPending = open && (funnelQuery.isLoading || formQuery.isLoading);
  const linkedForm = formQuery.data ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-xl flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <SheetTitle className="text-foreground flex items-center gap-2 text-base font-semibold">
            <span className="bg-primary/10 text-primary inline-flex size-8 items-center justify-center rounded-md">
              <UserPlus2 className="size-4" aria-hidden="true" />
            </span>
            New enquiry
          </SheetTitle>
          <SheetDescription className="text-muted-foreground text-xs">
            Add a prospective {personOne.toLowerCase()} to your funnel. They&apos;ll land in the
            first stage of your admissions process — you can move them forward from the detail view.
          </SheetDescription>
          <div className="pt-2">
            <AIDraftButton<EnquiryDraft>
              title="Draft an enquiry"
              placeholder="e.g. Riya Mehta called, wants evening slots, phone 98765 43210, heard from a friend"
              generate={generateEnquiryDraft}
              describe={describeEnquiryDraft}
              onApply={applyEnquiryDraft}
              disabled={isBusy}
              data-testid="enq-create-ai-draft"
            />
          </div>
        </SheetHeader>

        <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <Field
              id="enq-student-name"
              label={`${personOne} name`}
              required
              error={fieldErrors.student_name}
            >
              <Input
                id="enq-student-name"
                value={values.student_name}
                onChange={(e) => set("student_name")(e.target.value)}
                placeholder="e.g. Aarav Sharma"
                autoComplete="name"
                maxLength={PERSON_NAME_MAX}
                aria-invalid={fieldErrors.student_name ? true : undefined}
                aria-describedby={fieldErrors.student_name ? "enq-student-name-error" : undefined}
                required
                disabled={isBusy}
                data-testid="enq-create-student-name"
              />
            </Field>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                id="enq-student-email"
                label={`${personOne} email`}
                error={fieldErrors.student_email}
              >
                <Input
                  id="enq-student-email"
                  type="email"
                  value={values.student_email}
                  onChange={(e) => set("student_email")(e.target.value)}
                  placeholder="aarav@example.com"
                  autoComplete="email"
                  maxLength={EMAIL_MAX_LENGTH}
                  aria-invalid={fieldErrors.student_email ? true : undefined}
                  aria-describedby={
                    fieldErrors.student_email ? "enq-student-email-error" : undefined
                  }
                  disabled={isBusy}
                />
              </Field>
              <Field
                id="enq-student-phone"
                label={`${personOne} phone`}
                error={fieldErrors.student_phone}
              >
                <Input
                  id="enq-student-phone"
                  type="tel"
                  value={values.student_phone}
                  onChange={(e) => set("student_phone")(e.target.value)}
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={PHONE_MAX_LENGTH}
                  aria-invalid={fieldErrors.student_phone ? true : undefined}
                  aria-describedby={
                    fieldErrors.student_phone ? "enq-student-phone-error" : undefined
                  }
                  disabled={isBusy}
                />
              </Field>
            </div>

            {showParent && (
              <>
                <div className="border-border/60 border-t pt-5">
                  <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider">
                    Parent / Guardian
                  </p>
                  <p className="text-muted-foreground/80 text-xs">
                    Optional — leave blank if the student is the primary contact.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field id="enq-parent-name" label="Parent name" error={fieldErrors.parent_name}>
                    <Input
                      id="enq-parent-name"
                      value={values.parent_name}
                      onChange={(e) => set("parent_name")(e.target.value)}
                      placeholder="e.g. Priya Sharma"
                      maxLength={PERSON_NAME_MAX}
                      aria-invalid={fieldErrors.parent_name ? true : undefined}
                      aria-describedby={
                        fieldErrors.parent_name ? "enq-parent-name-error" : undefined
                      }
                      disabled={isBusy}
                    />
                  </Field>
                  <Field
                    id="enq-parent-phone"
                    label="Parent phone"
                    error={fieldErrors.parent_phone}
                  >
                    <Input
                      id="enq-parent-phone"
                      type="tel"
                      value={values.parent_phone}
                      onChange={(e) => set("parent_phone")(e.target.value)}
                      placeholder="+91 98765 43210"
                      inputMode="tel"
                      maxLength={PHONE_MAX_LENGTH}
                      aria-invalid={fieldErrors.parent_phone ? true : undefined}
                      aria-describedby={
                        fieldErrors.parent_phone ? "enq-parent-phone-error" : undefined
                      }
                      disabled={isBusy}
                    />
                  </Field>
                </div>
              </>
            )}

            <div className="border-border/60 border-t pt-5">
              <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider">
                Tracking
              </p>
            </div>

            <Field id="enq-source" label="Source" error={fieldErrors.source}>
              <Select
                value={values.source || undefined}
                onValueChange={(v) => set("source")(v as EnquirySource)}
                disabled={isBusy}
              >
                <SelectTrigger id="enq-source" data-testid="enq-create-source">
                  <SelectValue placeholder="How did they reach us?" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field id="enq-notes" label="Notes" error={fieldErrors.notes}>
              <Textarea
                id="enq-notes"
                value={values.notes}
                onChange={(e) => set("notes")(e.target.value)}
                placeholder="Anything the counsellor should know on first contact?"
                rows={3}
                maxLength={NOTES_MAX}
                disabled={isBusy}
                className="resize-none"
              />
            </Field>

            {/* Phase H1 FE-1 — initial-stage form section. We always
                render the section header (even while loading) so the
                IA isn't surprised by a tall form appearing late. */}
            <div className="border-border/60 border-t pt-5">
              <p className="text-muted-foreground flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider">
                <ClipboardList className="size-3" aria-hidden="true" />
                Initial stage form
              </p>
              <p className="text-muted-foreground/80 text-xs">
                {linkedForm
                  ? `Required — ${
                      showParent ? "the parent" : `the ${personOne.toLowerCase()}`
                    } would normally fill this on the apply portal. You're filling on their behalf.`
                  : isFormPending
                    ? "Checking whether your funnel has a form linked to the first stage…"
                    : "Your funnel's first stage has no linked form. The enquiry will land directly in the funnel."}
              </p>
            </div>

            {isFormPending && (
              <div
                className="border-border/60 bg-muted/30 flex items-center gap-2 rounded-md border px-3 py-2 text-xs"
                role="status"
              >
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                Loading the linked form…
              </div>
            )}

            {!isFormPending && linkedForm && (
              <div
                className="border-border/60 bg-card/40 rounded-md border p-4"
                data-testid="enq-create-stage-form"
              >
                <FormRenderer
                  form={linkedForm}
                  value={stagePayload}
                  onChange={setStagePayload}
                  files={stageFiles}
                  onFilesChange={setStageFiles}
                  serverErrors={stageFieldErrors}
                  onSubmit={submitFromRenderer}
                  showSubmit={false}
                  disabled={isBusy}
                />
              </div>
            )}
          </div>

          <div className="border-border/60 bg-card/40 border-t px-6 py-4">
            {topLevelError && (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>{topLevelError}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isBusy || isFormPending}
                data-testid="enq-create-submit"
              >
                {isBusy ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <UserPlus2 className="size-4" aria-hidden="true" />
                )}
                Create enquiry
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/** Tightly-scoped field row — Label on top, control below, optional
 * error message in `text-destructive` directly under the input. Keeps
 * vertical rhythm uniform across the form regardless of which control
 * the consumer drops in. */
function Field({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-foreground text-xs font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" className={cn("text-destructive text-xs leading-tight")}>
          {error}
        </p>
      )}
    </div>
  );
}
